<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col s9">
        <div class="row occupied-spacing-right">
          <div class="col s12 z-depth-1 white">
            <div class="section">
              <h5>{{$t('occupiedevents')}}</h5>
              <p>Min, max, average for {{ (this.railObject !== undefined ? this.railObject.Name : '') }}</p>
            </div>

            <highcharts :options="chartOptions" />

          </div>
        </div>
      </div>
      <div class="col s3">
        <div class="col s12 z-depth-1 white" style="height: 260px;">
          <h5>Filter</h5>

          <div class="input-field">
            <date-picker @input="dateChanged" v-model="date" :max-date="maxDate" @dateChanged="datePickerChanged" style="padding-right: 6px;" />
            <period-selector v-model="selectedPeriod" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OccupiedEventsService from '@/services/OccupiedEventsService'
import {Chart} from 'highcharts-vue'
import moment from 'moment'
import DatePicker from '@/components/switch/DatePicker'
import RailObjectService from '@/services/RailObjectService'
import AssetsProvider from '@/providers/AssetsProvider'
import PeriodSelector from '@/components/PeriodSelector'
import Highcharts from 'highcharts'

export default {
  name: 'AxleShunt',
  components: {
    HighChart: Chart,
    'datePicker': DatePicker,
    PeriodSelector
  },
  data () {
    return {
      r8assets: undefined,
      railObject: undefined,
      maxDate: new Date(),
      componentId: undefined,
      inputDate: '-24',
      date: {
        start: moment().endOf('day').subtract(1, 'w').format(),
        end: moment().endOf('day').format()
      },
      selectedPeriod: '1week',
      chartOptions: {
        chart: {
          zoomType: 'x',
          resetZoomButton: {
            position: {
              x: 0,
              y: -10
            }
          },
          animation: false,
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        plotOptions: {
          series: {
            allowPointSelect: true
          }
        },
        time: {
          useUTC: true
        },
        exporting: {
          enabled: false
        },
        title: {
          text: ''
        },
        accessibility: {
          enabled: false
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: ''
        },
        legend: {
          enabled: true
        },
        tooltip: {
          formatter: function () {
            return 'Date: ' + moment.utc(this.x).format('YYYY-MM-DD HH:mm:ss') + '<br/>' +
          'Value: ' + '<b>' + this.y + '</b>'
          },
          shared: true
        },
        yAxis: {
          title: {
            text: 'mA'
          } },
        xAxis: {
          title: {
            enabled: false
          },
          type: 'datetime',
          gridLineWidth: 1,
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%e %b \'%y',
            week: '%e %b \'%y',
            month: '%b \'%y',
            year: '%Y'
          }
        },
        series: [
          { name: 'min',
            turboThreshold: 1,
            data: [] },
          { name: 'avg',
            turboThreshold: 1,
            data: [] },
          { name: 'max',
            turboThreshold: 1,
            data: [] }
        ]
      }
    }
  },
  watch: {
    '$route.params.id': async function (newVal, oldVal) {
      if (newVal !== oldVal) {
        await this.getRailobject()
        await this.setAssetComponentId()
        this.dateChanged()
      }
    },
    selectedPeriod (period) {
      if (period === 'user_input') {
        return
      }
      const end = moment().endOf('day').format()
      const start = this.getStartDate(end)
      this.date = {
        start,
        end
      }
    }
  },
  mounted: async function () {
    M.FormSelect.init(document.querySelectorAll('select'))
    await this.getRailobject()
    await this.getR8Assets()
    await this.setAssetComponentId()
    this.dateChanged()
  },
  methods: {
    setAssetComponentId: async function () {
      var asset = await this.r8assets.find(r => r.uniqueId === this.railObject.UniqueId)
      if (asset && asset.children) {
        this.componentId = asset.children[0].uniqueId
      }
    },
    getR8Assets: async function () {
      await AssetsProvider.getAssets().then(result => {
        this.r8assets = result
      })
    },
    getRailobject: async function () {
      var railObjectId = this.$route.params.id
      if (railObjectId) {
        this.railObject = await RailObjectService.getRailObjectByRailObjectId(railObjectId)
      }
    },
    dateChanged: function () {
      var start = moment.utc(this.date.start).startOf('day').format() // todays date
      var end = moment.utc(this.date.end).endOf('day').format() // another date
      this.getOccupiedEvents(start, end)
    },
    getOccupiedEvents: async function (start, end) {
      if (this.railObject !== undefined) {
        this.chartOptions.series[0].data = []
        this.chartOptions.series[1].data = []
        this.chartOptions.series[2].data = []

        await OccupiedEventsService.GetOccupiedEventsAsync(this.railObject.UniqueId, this.componentId, 'mA_AVG', start + '-til-' + end).then(result => {
          this.chartOptions.series[1].data = this.createSeriesData(result.data)
        })

        await OccupiedEventsService.GetOccupiedEventsAsync(this.railObject.UniqueId, this.componentId, 'mA_MIN', start + '-til-' + end).then(result => {
          this.chartOptions.series[0].data = this.createSeriesData(result.data)
        })

        await OccupiedEventsService.GetOccupiedEventsAsync(this.railObject.UniqueId, this.componentId, 'mA_MAX', start + '-til-' + end).then(result => {
          this.chartOptions.series[2].data = this.createSeriesData(result.data)
        })
      }
    },
    createSeriesData: function (data) {
      var formattedData = []
      data.forEach(element => {
        formattedData.push([new Date(this.formatDateTime(element.dt)), JSON.parse(element.val)])
      })
      return formattedData
    },
    formatDateTime: (dateTime) => {
      return moment(String(dateTime)).format('YYYY-MM-DD HH:mm:ss')
    },
    datePickerChanged () {
      this.selectedPeriod = 'user_input'
    },
    getStartDate (endDate) {
      switch (this.selectedPeriod) {
        case '1week':
          return moment(endDate).subtract(1, 'weeks')
        case '2weeks':
          return moment(endDate).subtract(2, 'weeks')
        case '1month':
          return moment(endDate).subtract(1, 'months')
        case '3months':
          return moment(endDate).subtract(3, 'months')
        case '6months':
          return moment(endDate).subtract(6, 'months')
        case '1year':
          return moment(endDate).subtract(1, 'years')
        default:
          return moment(endDate).subtract(1, 'months')
      }
    }
  }
}
</script>

<style scoped>
.occupied-spacing-right {
  margin-right: 12px
}
.popover-container {
  display: inline-block;
}
</style>



// WEBPACK FOOTER //
// src/pages/axleshunt/Occupied/Occupied.vue